/**
 * Ticket imprimible (80mm térmico vía window.print + CSS @media print).
 *
 * El estilo está en `src/index.css` (regla `@media print` con `#print-area`).
 * Si la venta está anulada se imprime un sello "ANULADO" en el header.
 */

import { PAYMENT_METHOD_LABELS } from "@/lib/labels.ts";
import { formatPEN } from "@/lib/utils.ts";
import { CustomerDocType, SaleStatus, type SaleDetailOut } from "@/types/api.ts";

// Título del comprobante electrónico en la representación impresa.
const ELECTRONIC_TITLES: Record<string, string> = {
  boleta: "BOLETA DE VENTA ELECTRÓNICA",
  factura: "FACTURA ELECTRÓNICA",
};

const DOC_TYPE_LABELS: Record<string, string> = {
  [CustomerDocType.DNI]: "DNI",
  [CustomerDocType.RUC]: "RUC",
  [CustomerDocType.FOREIGN]: "Doc.",
};

interface Props {
  sale: SaleDetailOut;
  cashierName?: string;
  /** Datos de empresa. En una próxima ronda vendrán de GET /tenant. */
  company?: {
    legal_name: string;
    ruc: string;
    address?: string;
  };
}

export function Ticket({ sale, cashierName, company }: Props) {
  const electronicTitle = ELECTRONIC_TITLES[sale.document_type ?? ""];
  const docTitle =
    electronicTitle ?? ((sale.document_type ?? "").toUpperCase().replace("_", " ") || "TICKET");
  const edoc = sale.electronic_document ?? null;
  const sunatOk = edoc?.status === "accepted" || edoc?.status === "observed";
  const docNumber =
    sale.series_code && sale.document_number
      ? `${sale.series_code}-${String(sale.document_number).padStart(8, "0")}`
      : "—";
  const issuedAt = sale.issued_at ? new Date(sale.issued_at).toLocaleString("es-PE") : "—";
  const isVoided = sale.status === SaleStatus.VOIDED;

  return (
    <div id="print-area">
      <div className="ticket">
        <header className="text-center">
          <p className="font-bold">{company?.legal_name ?? "EMPRESA NO CONFIGURADA"}</p>
          {company?.ruc && <p>RUC {company.ruc}</p>}
          {company?.address && <p>{company.address}</p>}
          <hr className="my-2 border-dashed border-black" />
          <p className="font-bold">{docTitle}</p>
          <p>{docNumber}</p>
          {isVoided && (
            <p className="my-2 border-2 border-black p-1 text-base font-bold tracking-widest">
              *** ANULADO ***
            </p>
          )}
        </header>

        <section className="my-2 text-xs">
          <div className="flex justify-between">
            <span>Fecha:</span>
            <span>{issuedAt}</span>
          </div>
          {cashierName && (
            <div className="flex justify-between">
              <span>Cajero:</span>
              <span>{cashierName}</span>
            </div>
          )}
        </section>

        {sale.customer && (
          <>
            <hr className="border-dashed border-black" />
            <section className="my-2 text-xs">
              <p className="font-bold">Cliente</p>
              <p>{sale.customer.full_name}</p>
              <p>
                {DOC_TYPE_LABELS[sale.customer.doc_type] ?? sale.customer.doc_type}{" "}
                {sale.customer.doc_number}
              </p>
              {sale.customer.address && <p>{sale.customer.address}</p>}
            </section>
          </>
        )}

        <hr className="border-dashed border-black" />

        <table className="my-1 w-full text-xs">
          <thead>
            <tr className="border-b border-dashed border-black">
              <th className="text-left">Producto</th>
              <th className="text-right">Cant</th>
              <th className="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {sale.items.map((item) => (
              <tr key={item.id}>
                <td className="py-0.5">
                  {item.product_name}
                  <br />
                  <span className="text-[10px]">
                    SKU {item.product_sku} @ {formatPEN(item.unit_price)}
                  </span>
                </td>
                <td className="align-top text-right">{item.quantity}</td>
                <td className="align-top text-right">{formatPEN(item.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <hr className="border-dashed border-black" />

        <section className="my-1 text-xs">
          <Row label="Subtotal" value={formatPEN(sale.subtotal)} />
          <Row label="IGV" value={formatPEN(sale.igv_amount)} />
          {Number(sale.discount_total) > 0 && (
            <Row label="Descuento" value={`-${formatPEN(sale.discount_total)}`} />
          )}
          <div className="mt-1 flex justify-between border-t border-dashed border-black pt-1 font-bold">
            <span>TOTAL</span>
            <span>{formatPEN(sale.total)}</span>
          </div>
        </section>

        {sale.payments.length > 0 && (
          <>
            <hr className="border-dashed border-black" />
            <section className="my-1 text-xs">
              {sale.payments.map((p) => (
                <Row
                  key={p.id}
                  label={PAYMENT_METHOD_LABELS[p.method] ?? p.method}
                  value={formatPEN(p.amount)}
                />
              ))}
              {sale.payments.some((p) => Number(p.change_given) > 0) && (
                <Row
                  label="Vuelto"
                  value={formatPEN(
                    sale.payments.reduce((acc, p) => acc + Number(p.change_given), 0),
                  )}
                />
              )}
            </section>
          </>
        )}

        {electronicTitle && (
          <>
            <hr className="border-dashed border-black" />
            <section className="my-2 text-center text-[10px]">
              {sunatOk && edoc?.qr_image ? (
                <>
                  <img src={edoc.qr_image} alt="QR SUNAT" className="mx-auto h-28 w-28" />
                  <p className="mt-1">
                    Representación impresa de la {electronicTitle.toLowerCase()}
                  </p>
                  {edoc.hash_code && <p className="break-all">Hash: {edoc.hash_code}</p>}
                  {edoc.void_status === "accepted" && <p>Comunicada de baja a SUNAT</p>}
                </>
              ) : edoc?.status === "rejected" ? (
                <p>
                  Comprobante rechazado por SUNAT
                  {edoc.sunat_message ? `: ${edoc.sunat_message}` : ""}
                </p>
              ) : (
                <p>Comprobante electrónico en proceso de envío a SUNAT.</p>
              )}
            </section>
          </>
        )}

        <footer className="mt-3 text-center text-xs">
          <p>¡Gracias por su compra!</p>
          <p className="text-[10px]">{sale.id.slice(0, 8)}</p>
        </footer>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
